// Onboarding-specific auth: completely separate from admin AuthContext.
// Stores the JWT issued by /api/onboarding/auth/verify-code in localStorage,
// scoped to the public onboarding flow. NOT used by /admin.
import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { API_URL } from "../utils/api";

const STORAGE_KEY = "elido_onboarding_jwt";

const OnboardingAuthContext = createContext(null);

export function OnboardingAuthProvider({ children }) {
  const [token, setToken] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || null; } catch (e) { return null; }
  });
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);

  const persist = useCallback((newToken) => {
    if (newToken) {
      try { localStorage.setItem(STORAGE_KEY, newToken); } catch (e) { /* ignore */ }
    } else {
      try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }
    }
    setToken(newToken);
  }, []);

  const refreshUser = useCallback(async () => {
    if (!token) { setUser(null); return null; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/onboarding/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { persist(null); setUser(null); return null; }
      const data = await res.json();
      setUser(data);
      return data;
    } catch (e) {
      persist(null); setUser(null); return null;
    } finally {
      setLoading(false);
    }
  }, [token, persist]);

  useEffect(() => {
    if (token && !user) refreshUser();
  }, [token, user, refreshUser]);

  const requestCode = useCallback(async (email) => {
    const res = await fetch(`${API_URL}/api/onboarding/auth/request-code`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || "Kunde inte skicka kod");
    return data;
  }, []);

  const verifyCode = useCallback(async (email, code) => {
    const res = await fetch(`${API_URL}/api/onboarding/auth/verify-code`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, code }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || "Fel kod");
    persist(data.token);
    setUser(data.user);
    return data;
  }, [persist]);

  // Accept a JWT minted by our own backend (e.g. after the self-hosted Google
  // OAuth callback redirects back with the token in the URL fragment). Same
  // contract as verifyCode but no second backend round-trip needed since the
  // callback already issued the JWT.
  const applyToken = useCallback((jwt, email) => {
    if (!jwt) throw new Error("Token saknas");
    persist(jwt);
    if (email) {
      setUser({ email, role: "onboarding_user" });
    }
    return jwt;
  }, [persist]);

  const logout = useCallback(() => {
    persist(null);
    setUser(null);
  }, [persist]);

  // Always read the latest token from localStorage so callers immediately
  // after `persist(...)` see the new JWT — even before React state has
  // re-rendered. Fixes the post-Google-OAuth race that produced
  // "Saknar behörighet" because analyzeAfterAuth ran with the old closure.
  const authedFetch = useCallback(async (path, init = {}) => {
    let liveToken = token;
    try { liveToken = localStorage.getItem(STORAGE_KEY) || liveToken; } catch (_) { /* ignore */ }
    const headers = { ...(init.headers || {}) };
    if (liveToken) headers.Authorization = `Bearer ${liveToken}`;
    if (init.body && !headers["Content-Type"]) headers["Content-Type"] = "application/json";
    return fetch(`${API_URL}${path}`, { ...init, headers });
  }, [token]);

  const value = {
    token,
    user,
    loading,
    isAuthed: !!token,
    requestCode,
    verifyCode,
    applyToken,
    logout,
    refreshUser,
    authedFetch,
  };
  return (
    <OnboardingAuthContext.Provider value={value}>{children}</OnboardingAuthContext.Provider>
  );
}

export function useOnboardingAuth() {
  const ctx = useContext(OnboardingAuthContext);
  if (!ctx) throw new Error("useOnboardingAuth must be used inside OnboardingAuthProvider");
  return ctx;
}
